import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { trpc } from '../../../trpc/client';
import { format } from 'date-fns';
import { Edit2, Trash2, Plus, Search, Eye, AlertTriangle, FileText } from 'lucide-react';
import { toast } from 'sonner';
import * as AlertDialog from '@radix-ui/react-alert-dialog';

const AdminBlogPage = () => {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const utils = trpc.useContext();
    const [postToDelete, setPostToDelete] = useState<string | null>(null);

    const { data, isLoading } = trpc.blog.getAllPosts.useQuery({
        page,
        limit: 10,
        search: search || undefined,
    });

    const deleteMutation = trpc.blog.deletePost.useMutation({
        onSuccess: () => {
            toast.success('Post deleted successfully');
            utils.blog.getAllPosts.invalidate();
            setPostToDelete(null);
        },
        onError: (err: any) => {
            toast.error(`Error deleting post: ${err.message}`);
            setPostToDelete(null);
        }
    });

    const handleDeleteUserAction = (id: string) => {
        setPostToDelete(id);
    };

    const confirmDelete = async () => {
        if (postToDelete) {
            await deleteMutation.mutateAsync({ id: postToDelete });
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-border pb-6">
                <div>
                    <h1 className="text-3xl font-black text-foreground tracking-tight">Blog Management</h1>
                    <p className="text-muted-foreground mt-2">Manage your articles, news, and announcements.</p>
                </div>
                <Link
                    to="/admin/blog/create"
                    className="bg-primary text-primary-foreground px-6 py-3 rounded-xl font-bold hover:bg-primary/90 flex items-center gap-2 transition-all hover:scale-105 shadow-lg shadow-primary/20 self-start md:self-auto"
                >
                    <Plus className="w-5 h-5" /> Write New Post
                </Link>
            </div>

            {/* Filters */}
            <div className="bg-card p-4 rounded-2xl shadow-sm border border-border flex items-center gap-4">
                <div className="relative flex-grow max-w-md">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Search posts..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-11 pr-4 py-3 bg-background border border-border rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none text-foreground placeholder:text-muted-foreground transition-all"
                    />
                </div>
            </div>

            {/* Table */}
            <div className="bg-card rounded-2xl shadow-sm border border-border overflow-hidden">
                {isLoading ? (
                    <div className="p-12 space-y-4">
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="flex items-center gap-4 animate-pulse">
                                <div className="h-12 w-12 bg-muted rounded-lg" />
                                <div className="flex-1 space-y-2">
                                    <div className="h-4 w-1/3 bg-muted rounded" />
                                    <div className="h-3 w-1/4 bg-muted rounded" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <>
                        <table className="w-full">
                            <thead className="bg-muted/50 border-b border-border">
                                <tr>
                                    <th className="text-left py-4 px-6 text-xs font-bold text-muted-foreground uppercase tracking-wider">Title</th>
                                    <th className="text-left py-4 px-6 text-xs font-bold text-muted-foreground uppercase tracking-wider">Status</th>
                                    <th className="text-left py-4 px-6 text-xs font-bold text-muted-foreground uppercase tracking-wider">Author</th>
                                    <th className="text-left py-4 px-6 text-xs font-bold text-muted-foreground uppercase tracking-wider">Date</th>
                                    <th className="text-right py-4 px-6 text-xs font-bold text-muted-foreground uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {data?.items.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="p-20 text-center text-muted-foreground flex flex-col items-center justify-center gap-4">
                                            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center">
                                                <FileText className="w-8 h-8 opacity-50" />
                                            </div>
                                            <div className="space-y-1">
                                                <p className="text-lg font-bold text-foreground">No posts found</p>
                                                <p className="text-sm">Start writing your first story!</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    data?.items.map((post: any) => (
                                        <tr key={post.id} className="hover:bg-muted/30 transition-colors group">
                                            <td className="py-5 px-6 md:min-w-[300px]">
                                                <div className="font-bold text-foreground line-clamp-1 text-base" title={post.title}>{post.title}</div>
                                                <div className="text-xs text-muted-foreground font-mono mt-1 opacity-70 group-hover:opacity-100 transition-opacity">/{post.slug}</div>
                                            </td>
                                            <td className="py-5 px-6">
                                                {post.published ? (
                                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-green-500/10 text-green-600 border border-green-500/20">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 mr-2 animate-pulse" />
                                                        Published
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-amber-500/10 text-amber-600 border border-amber-500/20">
                                                        Draft
                                                    </span>
                                                )}
                                            </td>
                                            <td className="py-5 px-6 text-sm text-muted-foreground font-medium">
                                                {post.author.username}
                                            </td>
                                            <td className="py-5 px-6 text-sm text-muted-foreground">
                                                {format(new Date(post.createdAt), 'MMM d, yyyy')}
                                            </td>
                                            <td className="py-5 px-6 text-right space-x-2">
                                                <Link
                                                    to={`/blog/${post.slug}`}
                                                    target="_blank"
                                                    className="inline-flex items-center p-2 text-muted-foreground hover:text-primary rounded-lg hover:bg-primary/10 transition-colors"
                                                    title="View Live"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </Link>
                                                <Link
                                                    to={`/admin/blog/edit/${post.slug}`}
                                                    className="inline-flex items-center p-2 text-muted-foreground hover:text-primary rounded-lg hover:bg-primary/10 transition-colors"
                                                    title="Edit"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </Link>
                                                <button
                                                    onClick={() => handleDeleteUserAction(post.id)}
                                                    className="inline-flex items-center p-2 text-muted-foreground hover:text-destructive rounded-lg hover:bg-destructive/10 transition-colors"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </>
                )}
            </div>

            {/* Pagination */}
            {data && data.pages > 1 && (
                <div className="flex justify-center gap-3 mt-8">
                    <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="px-4 py-2 border border-border bg-card rounded-lg hover:bg-muted disabled:opacity-50 transition-colors text-sm font-medium"
                    >
                        Previous
                    </button>
                    <span className="px-4 py-2 text-muted-foreground bg-muted/20 rounded-lg text-sm font-medium">
                        Page {page} of {data.pages}
                    </span>
                    <button
                        onClick={() => setPage(p => Math.min(data.pages, p + 1))}
                        disabled={page === data.pages}
                        className="px-4 py-2 border border-border bg-card rounded-lg hover:bg-muted disabled:opacity-50 transition-colors text-sm font-medium"
                    >
                        Next
                    </button>
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            <AlertDialog.Root open={!!postToDelete} onOpenChange={(open) => !open && setPostToDelete(null)}>
                <AlertDialog.Portal>
                    <AlertDialog.Overlay className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 animate-in fade-in" />
                    <AlertDialog.Content className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border border-border bg-background p-6 shadow-lg duration-200 sm:rounded-lg animate-in fade-in zoom-in-95">
                        <div className="flex flex-col gap-2 text-center sm:text-left">
                            <AlertDialog.Title className="text-lg font-semibold text-foreground flex items-center gap-2">
                                <AlertTriangle className="w-5 h-5 text-destructive" />
                                Are you absolutely sure?
                            </AlertDialog.Title>
                            <AlertDialog.Description className="text-sm text-muted-foreground">
                                This action cannot be undone. This will permanently delete the blog post and remove it from our servers.
                            </AlertDialog.Description>
                        </div>
                        <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 gap-2">
                            <AlertDialog.Cancel asChild>
                                <button className="inline-flex h-10 items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-semibold ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50">
                                    Cancel
                                </button>
                            </AlertDialog.Cancel>
                            <AlertDialog.Action asChild>
                                <button
                                    onClick={confirmDelete}
                                    className="inline-flex h-10 items-center justify-center rounded-md bg-destructive px-4 py-2 text-sm font-semibold text-destructive-foreground ring-offset-background transition-colors hover:bg-destructive/90 disabled:pointer-events-none disabled:opacity-50"
                                >
                                    {deleteMutation.isLoading ? 'Deleting...' : 'Continue'}
                                </button>
                            </AlertDialog.Action>
                        </div>
                    </AlertDialog.Content>
                </AlertDialog.Portal>
            </AlertDialog.Root>
        </div>
    );
};

export default AdminBlogPage;
